import { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchAllPoolMetrics } from '../api/poolAnalytics';
import { fetchAllPoolAverages } from '../api/poolHistory';
import { POOLS } from '@sepori/shared';
import type { PoolData, PoolGroup } from '../api/types';

export function usePoolData() {
  const [poolGroups, setPoolGroups] = useState<PoolGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Get all pool addresses and metadata (memoized to prevent re-renders)
  const poolEntries = useMemo(() => Object.entries(POOLS), []);
  const poolAddresses = useMemo(() => poolEntries.map(([, pool]) => pool.pool), [poolEntries]);

  const processPoolData = useCallback((metricsMap: Record<string, any>, averagesMap: Record<string, any>): PoolGroup[] => {
    const groups: PoolGroup[] = [
      { type: 'slipstream', pools: [] },
      { type: 'basic', pools: [] }
    ];

    poolEntries.forEach(([, pool]) => {
      const metrics = metricsMap[pool.pool];
      const averages = averagesMap[pool.pool];

      // Always add the pool, even if metrics are missing (show as N/A)
      const rollingAverages: Record<string, number> = {};
      if (averages?.averages) {
        averages.averages.forEach((avg: any) => {
          const aprValue = typeof avg.avg_apr === 'string' ? parseFloat(avg.avg_apr) : avg.avg_apr;
          rollingAverages[avg.period] = isNaN(aprValue) ? 0 : aprValue;
        });
      }

      const poolData: PoolData = {
        poolName: pool.name,
        poolType: pool.kind,
        poolAddress: pool.pool,
        tvlUsd: metrics ? (typeof metrics.tvlUsd === 'string' ? parseFloat(metrics.tvlUsd) : metrics.tvlUsd) : 0,
        aprCurrent: metrics ? (typeof metrics.aprCurrentPct === 'string' ? parseFloat(metrics.aprCurrentPct) : metrics.aprCurrentPct) : 0,
        rollingAverages
      };

      const groupIndex = pool.kind === 'slipstream' ? 0 : 1;
      groups[groupIndex].pools.push(poolData);
    });

    return groups;
  }, [poolEntries]);

  const loadData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      // Fetch current metrics and rolling averages in parallel
      // fetchAllPoolMetrics now fetches all pools at once from pool-aggregator (more efficient)
      // Pass bypassCache=true when refreshing to force fresh data
      const [allMetrics, averagesMap] = await Promise.all([
        fetchAllPoolMetrics(isRefresh ? { bypassCache: true } : undefined),
        fetchAllPoolAverages(poolAddresses)
      ]);

      // Normalize allMetrics keys to lowercase for matching (processPoolData expects lowercase keys)
      const normalizedMetrics: Record<string, any> = {};
      Object.keys(allMetrics).forEach((key) => {
        normalizedMetrics[key.toLowerCase()] = allMetrics[key];
      });

      // Pass normalized metrics to processPoolData
      // processPoolData will handle missing entries by using 0 values (already implemented)
      const groups = processPoolData(normalizedMetrics, averagesMap);
      setPoolGroups(groups);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load pool data';
      setError(errorMessage);
      console.error('Error loading pool data:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [poolAddresses, processPoolData]);

  const refreshCurrentData = useCallback(() => {
    loadData(true);
  }, [loadData]);

  // Load data on mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  return {
    poolGroups,
    loading,
    error,
    refreshing,
    refreshCurrentData
  };
}
